// Audio Book Tag Parsing and Categorization Utilities

export type BookLanguage = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'nl' | 'ja' | 'ko' | 'zh' | 'ar' | 'pl' | 'lt';

export const LANGUAGE_INFO: Record<BookLanguage, { name: string; code: string; flag: string; gradient: string }> = {
  en: { name: 'English', code: 'EN', flag: '🇺🇸', gradient: 'linear-gradient(135deg, #667eea, #764ba2)' },
  es: { name: 'Spanish', code: 'ES', flag: '🇪🇸', gradient: 'linear-gradient(135deg, #f093fb, #f5576c)' },
  fr: { name: 'French', code: 'FR', flag: '🇫🇷', gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)' },
  de: { name: 'German', code: 'DE', flag: '🇩🇪', gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)' },
  it: { name: 'Italian', code: 'IT', flag: '🇮🇹', gradient: 'linear-gradient(135deg, #fa709a, #fee140)' },
  pt: { name: 'Portuguese', code: 'PT', flag: '🇧🇷', gradient: 'linear-gradient(135deg, #30cfd0, #330867)' },
  nl: { name: 'Dutch', code: 'NL', flag: '🇳🇱', gradient: 'linear-gradient(135deg, #a8edea, #fed6e3)' },
  ja: { name: 'Japanese', code: 'JA', flag: '🇯🇵', gradient: 'linear-gradient(135deg, #ff9a9e, #fecfef)' },
  ko: { name: 'Korean', code: 'KO', flag: '🇰🇷', gradient: 'linear-gradient(135deg, #ffecd2, #fcb69f)' },
  zh: { name: 'Mandarin', code: 'ZH', flag: '🇨🇳', gradient: 'linear-gradient(135deg, #ff6e7f, #bfe9ff)' },
  ar: { name: 'Arabic', code: 'AR', flag: '🇸🇦', gradient: 'linear-gradient(135deg, #e0c3fc, #8ec5fc)' },
  pl: { name: 'Polish', code: 'PL', flag: '🇵🇱', gradient: 'linear-gradient(135deg, #f093fb, #f5576c)' },
  lt: { name: 'Lithuanian', code: 'LT', flag: '🇱🇹', gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)' },
};

export const AGE_GROUPS = [
  { id: '0-3', label: 'Toddlers (0-3)', color: '#A8D8E6', range: [0, 3] },
  { id: '3-5', label: 'Preschool (3-5)', color: '#FFB6D9', range: [3, 5] },
  { id: '5-7', label: 'Early Readers (5-7)', color: '#C9B6E4', range: [5, 7] },
  { id: '7-9', label: 'Growing Up (7-9)', color: '#A8E6CF', range: [7, 9] },
  { id: '9-12', label: 'Tweens (9-12)', color: '#FFD8A8', range: [9, 12] },
  { id: 'all', label: 'All Ages', color: '#E0E0E0', range: [0, 100] },
];

export const THEME_KEYWORDS: Record<string, string[]> = {
  'Adventure & Exploration': ['adventure', 'explore', 'journey', 'quest', 'discovery', 'travel', 'expedition'],
  'Bedtime Stories': ['bedtime', 'sleep', 'night', 'dream', 'lullaby', 'goodnight', 'peaceful'],
  'Learning & Education': ['learning', 'education', 'school', 'teach', 'study', 'lesson', 'knowledge', 'math', 'science'],
  'Magic & Fantasy': ['magic', 'fantasy', 'wizard', 'fairy', 'enchanted', 'mystical', 'unicorn', 'dragon'],
  'Animals & Nature': ['animal', 'nature', 'wildlife', 'forest', 'ocean', 'pets', 'zoo', 'farm'],
  'Friendship & Family': ['friend', 'friendship', 'family', 'together', 'love', 'caring', 'sharing', 'kindness'],
  'Emotions & Feelings': ['emotion', 'feeling', 'happy', 'sad', 'brave', 'scared', 'confident', 'empathy'],
  'Science & Technology': ['science', 'technology', 'robot', 'space', 'experiment', 'invention', 'discovery'],
  'Music & Arts': ['music', 'art', 'sing', 'dance', 'creative', 'painting', 'drawing'],
  'Sports & Activities': ['sport', 'play', 'game', 'active', 'exercise', 'team', 'competition'],
};

/**
 * Parse hashtags string into array of clean tags
 */
export function parseHashtags(hashtags: string): string[] {
  if (!hashtags || hashtags.trim() === '') return [];
  
  // Split by common separators and clean
  return hashtags
    .split(/[,\s#]+/)
    .map(tag => tag.trim().toLowerCase())
    .filter(tag => tag.length > 0 && tag.length < 30); // Filter out empty and too long
}

/**
 * Categorize tags into predefined themes
 */
export function categorizeTheme(tags: string[]): string[] {
  const themes = new Set<string>();
  
  tags.forEach(tag => {
    for (const [theme, keywords] of Object.entries(THEME_KEYWORDS)) {
      if (keywords.some(keyword => tag.includes(keyword) || keyword.includes(tag))) {
        themes.add(theme);
      }
    }
  });
  
  return Array.from(themes);
}

/**
 * Extract age group from ageBucket string
 * Examples: "0-3", "3-5 years", "Ages 5-7", null
 */
export function parseAgeGroup(ageBucket: string | null): string {
  if (!ageBucket) return 'all';
  
  // Extract numbers from string
  const numbers = ageBucket.match(/\d+/g);
  if (!numbers || numbers.length === 0) return 'all';
  
  const minAge = parseInt(numbers[0]);
  const maxAge = numbers.length > 1 ? parseInt(numbers[1]) : minAge;
  
  // Match to predefined age groups
  for (const group of AGE_GROUPS) {
    const [groupMin, groupMax] = group.range;
    if (minAge >= groupMin && maxAge <= groupMax) {
      return group.id;
    }
  }
  
  // Find closest match
  for (const group of AGE_GROUPS) {
    const [groupMin, groupMax] = group.range;
    if (minAge <= groupMax && maxAge >= groupMin) {
      return group.id;
    }
  }
  
  return 'all';
}

/**
 * Get language display information
 */
export function getLanguageInfo(language: BookLanguage | string): { name: string; code: string; flag: string; gradient: string } {
  return LANGUAGE_INFO[language as BookLanguage] || LANGUAGE_INFO.en;
}

/**
 * Get age group display information
 */
export function getAgeGroupInfo(ageGroupId: string) {
  return AGE_GROUPS.find(g => g.id === ageGroupId) || AGE_GROUPS[AGE_GROUPS.length - 1];
}

/**
 * Count books by category
 */
export function countByCategory<T>(
  books: T[],
  categoryFn: (book: T) => string | string[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  
  books.forEach(book => {
    const categories = categoryFn(book);
    const categoryArray = Array.isArray(categories) ? categories : [categories];
    
    categoryArray.forEach(category => {
      counts[category] = (counts[category] || 0) + 1;
    });
  });
  
  return counts;
}

